import React, { useEffect, useRef, useState } from "react";
import cs from "classnames";
import { Spinner } from "reactstrap";

import { HostedPage } from "../../api/grpc/subscription/subscription";
import { Button } from "../shared/Button/Button";

const CONTAINER_ID = "product-store-checkout";

/** The parts of a Chargebee.js checkout object used here. */
interface ChargebeeCheckout {
  mount: (selector: string) => void;
  unmount?: () => void;
}

interface Props {
  /** Creates a new hosted checkout page; called again on retry. */
  loadHostedPage: () => Promise<Pick<HostedPage, "url">>;
  /** Payment went through. The checkout stays mounted for Chargebee's thank-you page. */
  onSuccess: () => void;
  /** Chargebee's own close button, and the error's way out. */
  onBack: () => void;
  /** The error's way-out button. */
  backLabel?: string;
}

/**
 * Chargebee's hosted checkout, mounted in place with Chargebee.js
 * (embedded checkout). Each attempt creates a new session.
 */
export const ProductStoreEmbeddedCheckout = ({
  loadHostedPage,
  onSuccess,
  onBack,
  backLabel = "Back to order",
}: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading"
  );
  const [attempt, setAttempt] = useState(0);
  const [height, setHeight] = useState<number>();
  // The latest props, so a re-render doesn't start a new session.
  const latest = useRef({ loadHostedPage, onSuccess, onBack });
  latest.current = { loadHostedPage, onSuccess, onBack };

  useEffect(() => {
    let cancelled = false;
    let checkout: ChargebeeCheckout | undefined;
    const container = containerRef.current;
    const ifActive =
      <T extends unknown[]>(callback: (...args: T) => void) =>
      (...args: T) => {
        if (!cancelled) {
          callback(...args);
        }
      };
    const fail = ifActive((error: unknown) => {
      console.error("Product Store checkout failed", error);
      setStatus("error");
    });

    setStatus("loading");

    const mount = async () => {
      try {
        const { url } = await latest.current.loadHostedPage();

        if (!url) {
          throw new Error("The checkout page has no URL.");
        }

        checkout = await window.Chargebee.getInstance().createCheckout({
          url,
          callbacks: {
            loaded: ifActive(() => setStatus("ready")),
            resize: ifActive((nextHeight: number) => setHeight(nextHeight)),
            success: ifActive(() => latest.current.onSuccess()),
            close: ifActive(() => latest.current.onBack()),
            error: fail,
          },
        });

        if (cancelled) {
          return;
        }

        if (!checkout) {
          throw new Error("Chargebee didn't create the checkout.");
        }

        checkout.mount(`#${CONTAINER_ID}`);
      } catch (error) {
        fail(error);
      }
    };

    void mount();

    return () => {
      cancelled = true;
      checkout?.unmount?.();
      // Removes Chargebee's iframe; React renders nothing inside it.
      container?.replaceChildren();
    };
  }, [attempt]);

  return (
    <div className="ProductStoreEmbeddedCheckout">
      {status === "loading" && (
        <div className="ProductStoreEmbeddedCheckout__loading" role="status">
          <Spinner size="sm" color="primary" />
          Loading secure checkout…
        </div>
      )}
      {status === "error" && (
        <div className="ProductStoreEmbeddedCheckout__error" role="alert">
          <p>We couldn't open checkout. Try again in a moment.</p>
          <div className="ProductStoreEmbeddedCheckout__actions">
            <Button
              title="Try again"
              onClick={() => setAttempt((count) => count + 1)}
            />
            <Button
              title={backLabel}
              color="outline-secondary"
              onClick={onBack}
            />
          </div>
        </div>
      )}
      {/* Collapsed, not hidden, until loaded: Chargebee needs its width. */}
      <div
        id={CONTAINER_ID}
        ref={containerRef}
        className={cs("ProductStoreEmbeddedCheckout__frame", {
          "ProductStoreEmbeddedCheckout__frame--pending": status !== "ready",
        })}
        // Chargebee reports its height on every step.
        style={status === "ready" && height ? { height } : undefined}
      />
    </div>
  );
};
